import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import AnalysisTablePalette from '../components/AnalysisTablePalette';
import './TipsTest.css';

const AYLAR = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const STAT_LABELS = {
    ms1: 'MS 1',
    msx: 'MS X',
    ms2: 'MS 2',
    iy05: 'İY 0.5 Üst',
    iy15: 'İY 1.5 Üst',
    ms25: 'MS 2.5 Üst',
    ms35: 'MS 3.5 Üst',
    kgv: 'KG Var',
    second_half_goal: '2. Yarı +1 Gol',
};

const formatTarih = (value) => {
    if (!value || value.length < 10) return value || '';
    const year = value.slice(0, 4);
    const month = parseInt(value.slice(5, 7), 10);
    const day = parseInt(value.slice(8, 10), 10);
    if (!month || !day) return value;
    return `${day} ${AYLAR[month - 1]} ${year}`;
};

const StatCard = ({ label, value }) => (
    <div className="tt2-pct-card">
        <span className="tt2-pct-label">{label}</span>
        <span className="tt2-pct-value">%{value.percentage}</span>
        <span className="tt2-pct-sub">{value.success}/{value.n} maç</span>
    </div>
);

const TrendCard = ({ title, items }) => (
    <div className="tt2-trend-card">
        <h5>{title}</h5>
        {items.length === 0 && <p>Trend verisi bulunamadı.</p>}
        {items.map((item, index) => (
            <div className="tt2-trend-row" key={item.score}>
                <span className="tt2-rank">{index + 1}</span>
                <strong>{item.score}</strong>
                <span>{item.count} maç · %{item.percentage}</span>
            </div>
        ))}
    </div>
);

const TipsTest2 = () => {
    const [bookmakers, setBookmakers] = useState([]);
    const [bookmakerId, setBookmakerId] = useState('');
    const [bookmakerLoading, setBookmakerLoading] = useState(true);
    const [matches, setMatches] = useState([]);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [liveScore, setLiveScore] = useState('');
    const [analysis, setAnalysis] = useState(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        const loadBookmakers = async () => {
            setBookmakerLoading(true);
            setError('');
            try {
                const response = await fetch('/api/tipstest2/bookmakers');
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Büro listesi yüklenemedi.');
                if (!Array.isArray(payload)) throw new Error('Geçersiz büro listesi.');
                if (cancelled) return;

                setBookmakers(payload);
                const firstAvailable = payload.find((bookmaker) => bookmaker.available);
                if (firstAvailable) setBookmakerId(String(firstAvailable.id));
                else setError('Kullanılabilir yabancı büro bulunamadı.');
            } catch (loadError) {
                if (!cancelled) setError(loadError.message || 'Büro listesi yüklenemedi.');
            } finally {
                if (!cancelled) setBookmakerLoading(false);
            }
        };

        loadBookmakers();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!bookmakerId) return undefined;
        let cancelled = false;
        setMatchesLoading(true);
        setMatches([]);
        setSelectedId('');
        setAnalysis(null);
        setError('');

        fetch(`/api/tipstest2/matches?bookmaker=${encodeURIComponent(bookmakerId)}`)
            .then(async (response) => {
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Maçlar yüklenemedi.');
                return payload;
            })
            .then((payload) => {
                if (!cancelled) setMatches(payload.maclar || []);
            })
            .catch((loadError) => {
                if (!cancelled) setError(loadError.message || 'Sunucuya ulaşılamadı.');
            })
            .finally(() => {
                if (!cancelled) setMatchesLoading(false);
            });

        return () => { cancelled = true; };
    }, [bookmakerId]);

    const dateGroups = useMemo(() => {
        const groups = [];
        matches.forEach((match) => {
            let group = groups.find((item) => item.date === match.tarih);
            if (!group) {
                group = { date: match.tarih, matches: [] };
                groups.push(group);
            }
            group.matches.push(match);
        });
        return groups;
    }, [matches]);

    const selectedMatch = useMemo(
        () => matches.find((match) => String(match.id) === selectedId),
        [matches, selectedId]
    );

    const matchOptions = useMemo(
        () => dateGroups.map((group) => ({
            label: formatTarih(group.date),
            options: group.matches.map((match) => ({
                value: String(match.id),
                label: `${match.saat ? `${match.saat} | ` : ''}${match.ev} - ${match.dep} (${match.o1} / ${match.ox} / ${match.o2})`,
            })),
        })),
        [dateGroups]
    );

    const selectedOption = selectedMatch ? {
        value: String(selectedMatch.id),
        label: `${selectedMatch.saat ? `${selectedMatch.saat} | ` : ''}${selectedMatch.ev} - ${selectedMatch.dep} (${selectedMatch.o1} / ${selectedMatch.ox} / ${selectedMatch.o2})`,
    } : null;

    const statOrder = analysis && analysis.mode === 'live'
        ? ['second_half_goal', 'ms25', 'ms35', 'kgv']
        : ['ms1', 'msx', 'ms2', 'iy05', 'iy15', 'ms25', 'ms35', 'kgv'];

    const handleAnalyze = async (event) => {
        event.preventDefault();
        if (!bookmakerId || !selectedId) {
            setError('Lütfen yabancı büro ve maç seçin.');
            return;
        }
        if (liveScore.trim() && !/^\d{1,2}\s*-\s*\d{1,2}$/.test(liveScore.trim())) {
            setError('Canlı ilk yarı skorunu 0-0 biçiminde girin.');
            return;
        }

        setAnalysisLoading(true);
        setAnalysis(null);
        setError('');
        const params = new URLSearchParams({
            bookmaker: bookmakerId,
            id: selectedId,
        });
        if (liveScore.trim()) params.set('iy_skor', liveScore.trim());

        try {
            const response = await fetch(`/api/tipstest2/analyze?${params.toString()}`);
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.error || 'Analiz sırasında bir hata oluştu.');
            setAnalysis(payload);
        } catch (analysisError) {
            setError(analysisError.message || 'Sunucuya ulaşılamadı.');
        } finally {
            setAnalysisLoading(false);
        }
    };

    return (
        <div className="tips-lab-page">
            <div className="breadcrumb-section breadcrumb-bg">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2 text-center">
                            <div className="breadcrumb-text">
                                <p>Yabancı Oran Analiz Motoru</p>
                                <h1>Zeta Analiz</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tips-lab-content">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h3>Zeta <span className="orange-text">Analiz</span></h3>
                                <p>
                                    Yabancı oranlar için maç öncesi ve canlı analiz sonuçları.
                                </p>
                            </div>

                            {error && <div className="tipstest-error tt2-error">{error}</div>}

                            <form className="tipstest-panel tt2-panel" onSubmit={handleAnalyze}>
                                <div className="tt2-v68-form-grid">
                                    <div className="tt2-select-field">
                                        <label htmlFor="tt2-bookmaker">Yabancı Büro</label>
                                        <select
                                            id="tt2-bookmaker"
                                            value={bookmakerId}
                                            onChange={(event) => setBookmakerId(event.target.value)}
                                            disabled={bookmakerLoading}
                                        >
                                            <option value="">
                                                {bookmakerLoading ? 'Bürolar yükleniyor...' : 'Büro seçin...'}
                                            </option>
                                            {bookmakers.map((bookmaker) => (
                                                <option
                                                    key={bookmaker.id}
                                                    value={bookmaker.id}
                                                    disabled={!bookmaker.available}
                                                >
                                                    {bookmaker.name}
                                                    {bookmaker.available ? '' : ' (veri tablosu yok)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="tt2-select-field tt2-match-field">
                                        <label htmlFor="tt2-match">Yaklaşan Maç</label>
                                        <Select
                                            inputId="tt2-match"
                                            value={selectedOption}
                                            onChange={(option) => setSelectedId(option ? option.value : '')}
                                            options={matchOptions}
                                            isClearable
                                            isSearchable
                                            isDisabled={matchesLoading || !bookmakerId}
                                            placeholder={matchesLoading
                                                ? 'Maçlar yükleniyor...'
                                                : matches.length
                                                    ? 'Maç adı yazarak arayın...'
                                                    : 'Yaklaşan maç bulunamadı'}
                                            noOptionsMessage={() => 'Maç bulunamadı'}
                                            classNamePrefix="tt1-react-select"
                                        />
                                    </div>

                                    <div className="tipstest-field tt2-live-field">
                                        <label htmlFor="tt2-live-score">Canlı İY Skoru <span>(isteğe bağlı)</span></label>
                                        <input
                                            id="tt2-live-score"
                                            type="text"
                                            inputMode="text"
                                            placeholder="Örn. 0-0"
                                            value={liveScore}
                                            onChange={(event) => setLiveScore(event.target.value)}
                                            maxLength="5"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="boxed-btn tt2-analyze-btn"
                                        disabled={analysisLoading || !selectedId}
                                    >
                                        {analysisLoading ? 'Analiz ediliyor...' : 'Analiz Et'}
                                    </button>
                                </div>

                                {selectedMatch && (
                                    <div className="tt2-selected-summary">
                                        <span>{selectedMatch.ev} - {selectedMatch.dep}</span>
                                        <strong>1 {selectedMatch.o1} · X {selectedMatch.ox} · 2 {selectedMatch.o2}</strong>
                                    </div>
                                )}
                            </form>

                            {analysis && (
                                <div className="tipstest-results tt2-results">
                                    <div className="tt2-result-topline">
                                        <span className={`tt2-mode-badge ${analysis.mode === 'live' ? 'tt2-mode-live' : ''}`}>
                                            {analysis.mode === 'live' ? `Canlı · İY ${analysis.live_score}` : 'Maç Öncesi'}
                                        </span>
                                        <span>{analysis.bookmaker_name} açılış oranları</span>
                                    </div>

                                    <div className="tipstest-matchup">
                                        <span className="tipstest-team-name">{analysis.mac.ev}</span>
                                        <span className="tipstest-matchup-vs">vs</span>
                                        <span className="tipstest-team-name">{analysis.mac.dep}</span>
                                    </div>
                                    <p className="tt2-mac-detay">
                                        {formatTarih(analysis.mac.tarih)}
                                        {analysis.mac.saat ? ` · ${analysis.mac.saat}` : ''}
                                        {analysis.mac.lig ? ` · ${analysis.mac.lig}` : ''}
                                        {' · '}{analysis.mac.o1} / {analysis.mac.ox} / {analysis.mac.o2}
                                    </p>

                                    <section className="tt2-stat-section">
                                        <div className="tt2-section-heading">
                                            <div>
                                                <h4>{analysis.mode === 'live' ? 'Canlı Maç İstatistikleri' : 'Maç Öncesi İstatistikleri'}</h4>
                                            </div>
                                        </div>
                                        <div className="tt2-pct-grid tt2-v68-stats-grid">
                                            {statOrder.map((key) => (
                                                <StatCard key={key} label={STAT_LABELS[key] || key} value={analysis.stats[key]} />
                                            ))}
                                        </div>
                                    </section>

                                    <section>
                                        <div className="tt2-section-heading">
                                            <div>
                                                <h4>Geçmiş Maçlar</h4>
                                            </div>
                                        </div>
                                        <AnalysisTablePalette>
                                            <table className="table tt2-table tt2-v68-table">
                                                <thead>
                                                    <tr>
                                                        <th>Tarih</th><th>Lig</th><th>Maç</th>
                                                        <th>1</th><th>X</th><th>2</th>
                                                        <th>İY</th><th>MS</th><th>İY/MS</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {analysis.matches.map((match) => (
                                                        <tr key={match.id}>
                                                            <td>{formatTarih(match.tarih)}</td>
                                                            <td>{match.lig || '—'}</td>
                                                            <td className="tt2-mac-adi">{match.ev} - {match.dep}</td>
                                                            <td>{match.o1}</td><td>{match.ox}</td><td>{match.o2}</td>
                                                            <td>{match.iy}</td><td><strong>{match.ms}</strong></td>
                                                            <td>{match.htft}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </AnalysisTablePalette>
                                    </section>

                                    <section className="tt2-trends-section">
                                        <div className="tt2-section-heading">
                                            <div>
                                                <h4>Skor Trendleri</h4>
                                            </div>
                                        </div>
                                        <div className="tt2-trends-grid">
                                            <TrendCard title="İlk Yarı Skor Trendi" items={analysis.trends.ht} />
                                            <TrendCard title="Maç Sonu Skor Trendi" items={analysis.trends.ft} />
                                        </div>
                                    </section>

                                    <p className="tipstest-disclaimer">
                                        Bu analiz geçmiş maç sonuçlarına dayalı istatistiksel bir çalışmadır; kesinlik garantisi vermez.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TipsTest2;
